import { isPlatformBrowser } from '@angular/common';
import { computed, inject, Injectable, PLATFORM_ID, signal } from '@angular/core';

import { ClientSettingsService } from './client-settings.service';
import { CustomerService } from './customer.service';

/**
 * Retorna o primeiro valor não vazio (ignora null/undefined e strings em branco).
 * Usado no lugar de `||` para evitar o alerta do Sonar sem perder o fallback
 * para string vazia, que `??` sozinho não cobre.
 */
function firstNonEmpty(...values: (string | null | undefined)[]): string {
  for (const value of values) {
    if (value?.trim()) {
      return value;
    }
  }

  return '';
}

interface StoredClientLogo {
  clientId: string;
  companyName: string;
  logoUrl: string | null;
  logoCachedAt: number;
}

@Injectable({
  providedIn: 'root',
})
export class ClientLogoService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly customerService = inject(CustomerService);
  private readonly clientSettingsService = inject(ClientSettingsService);

  private readonly isBrowser = isPlatformBrowser(this.platformId);

  private readonly storagePrefix = 'client-logo';
  private readonly legacyStoragePrefix = 'company-brand';

  /**
   * Tempo máximo de reutilização dos dados
   * armazenados no navegador.
   */
  private readonly logoCacheDuration = 50 * 60 * 1000;

  private readonly currentClientId = signal<string | null>(null);

  /**
   * Invalida respostas antigas que ainda estejam
   * em processamento.
   */
  private apiRequestVersion = 0;

  readonly companyName = signal('');
  readonly companyLogoUrl = signal<string | null>(null);
  readonly companyLogoLoadFailed = signal(false);

  readonly companyDisplayName = computed(() => {
    return this.companyName().trim() || 'TRACKGAINS®';
  });

  initialize(clientId: string | null | undefined): void {
    const normalizedClientId = clientId?.trim() ?? '';

    if (!normalizedClientId) {
      this.clearState();
      return;
    }

    if (this.currentClientId() === normalizedClientId) {
      return;
    }

    this.apiRequestVersion++;
    this.currentClientId.set(normalizedClientId);
    this.companyLogoLoadFailed.set(false);

    const storedClientLogo = this.getStoredClientLogo(normalizedClientId);

    if (storedClientLogo) {
      this.applyClientLogo(storedClientLogo.companyName, storedClientLogo.logoUrl);
    } else {
      this.companyName.set('');
      this.companyLogoUrl.set(null);
    }

    this.refresh(normalizedClientId);
  }

  /**
   * Busca o nome e a logo atualizados no backend.
   *
   * Quando a API retorna uma URL, uma versão é
   * adicionada para impedir o navegador de reutilizar
   * a imagem anterior.
   */
  async refresh(clientId?: string | null): Promise<void> {
    const normalizedClientId = clientId?.trim() ?? this.currentClientId() ?? '';

    if (!normalizedClientId) {
      return;
    }

    const requestVersion = ++this.apiRequestVersion;
    const storedClientLogo = this.getStoredClientLogo(normalizedClientId);

    try {
      const [customer, settings] = await Promise.all([
        this.customerService.getCustomerById(normalizedClientId),
        this.clientSettingsService.getByClientId(normalizedClientId),
      ]);

      if (requestVersion !== this.apiRequestVersion || this.currentClientId() !== normalizedClientId) {
        return;
      }

      // Se a API retornar nome em branco (dado ruim), cai para o nome em cache.
      const companyName = firstNonEmpty(customer?.nome, storedClientLogo?.companyName);

      /**
       * Quando settings existe, seu valor é a fonte
       * oficial. LogoUrl nulo significa que a logo
       * foi removida.
       */
      const logoUrl = settings
        ? this.createVersionedLogoUrl(settings.logoUrl ?? null)
        : (storedClientLogo?.logoUrl ?? null);

      this.applyClientLogo(companyName, logoUrl);

      this.saveStoredClientLogo({
        clientId: normalizedClientId,
        companyName,
        logoUrl,
        logoCachedAt: logoUrl ? Date.now() : 0,
      });
    } catch {
      /**
       * Mantém os dados recuperados anteriormente
       * quando a API não estiver disponível.
       */
      if (!storedClientLogo && this.currentClientId() === normalizedClientId) {
        this.companyName.set('');
        this.companyLogoUrl.set(null);
      }
    }
  }

  /**
   * Atualiza imediatamente a logo após upload ou
   * substituição.
   *
   * Mesmo que o backend devolva a mesma URL, o
   * parâmetro de versão altera o src da imagem.
   */
  updateClientLogo(clientId: string, companyName: string, logoUrl: string | null): void {
    const normalizedClientId = clientId.trim();

    if (!normalizedClientId) {
      return;
    }

    this.apiRequestVersion++;

    const normalizedCompanyName = companyName.trim();

    const versionedLogoUrl = this.createVersionedLogoUrl(logoUrl);

    if (this.currentClientId() === normalizedClientId) {
      this.applyClientLogo(normalizedCompanyName, versionedLogoUrl);
    }

    this.saveStoredClientLogo({
      clientId: normalizedClientId,
      companyName: normalizedCompanyName,
      logoUrl: versionedLogoUrl,
      logoCachedAt: versionedLogoUrl ? Date.now() : 0,
    });
  }

  /**
   * Atualiza somente o nome, preservando a logo.
   */
  updateClientName(clientId: string, companyName: string): void {
    const normalizedClientId = clientId.trim();

    if (!normalizedClientId) {
      return;
    }

    const storedClientLogo = this.getStoredClientLogo(normalizedClientId);

    const currentLogoUrl =
      this.currentClientId() === normalizedClientId ? this.companyLogoUrl() : (storedClientLogo?.logoUrl ?? null);

    this.apiRequestVersion++;

    const normalizedCompanyName = companyName.trim();

    if (this.currentClientId() === normalizedClientId) {
      this.companyName.set(normalizedCompanyName);
    }

    this.saveStoredClientLogo({
      clientId: normalizedClientId,
      companyName: normalizedCompanyName,
      logoUrl: currentLogoUrl,
      logoCachedAt: currentLogoUrl ? (storedClientLogo?.logoCachedAt ?? Date.now()) : 0,
    });
  }

  /**
   * Executado quando o navegador não consegue
   * carregar a imagem.
   */
  async handleLogoError(): Promise<void> {
    const clientId = this.currentClientId();

    this.companyLogoLoadFailed.set(true);
    this.companyLogoUrl.set(null);

    if (!clientId) {
      return;
    }

    this.invalidateStoredLogo(clientId);

    await this.refresh(clientId);
  }

  /**
   * Remove a logo armazenada e limpa o estado.
   */
  clear(): void {
    const clientId = this.currentClientId();

    this.apiRequestVersion++;

    if (clientId) {
      this.removeStoredClientLogo(clientId);
    }

    this.clearState();
  }

  /**
   * Limpa apenas o estado em memória.
   */
  clearState(): void {
    this.apiRequestVersion++;

    this.currentClientId.set(null);
    this.companyName.set('');
    this.companyLogoUrl.set(null);
    this.companyLogoLoadFailed.set(false);
  }

  /**
   * Remove todas as chaves relacionadas às logos
   * armazenadas no navegador.
   */
  clearAllStoredClientLogos(): void {
    if (!this.isBrowser) {
      return;
    }

    try {
      const keysToRemove: string[] = [];

      for (let index = 0; index < localStorage.length; index++) {
        const key = localStorage.key(index);

        if (key?.startsWith(`${this.storagePrefix}:`) || key?.startsWith(`${this.legacyStoragePrefix}:`)) {
          keysToRemove.push(key);
        }
      }

      keysToRemove.forEach((key) => {
        localStorage.removeItem(key);
      });
    } catch {
      // LocalStorage indisponível.
    }
  }

  private applyClientLogo(companyName: string, logoUrl: string | null): void {
    this.companyName.set(companyName.trim());
    this.companyLogoUrl.set(logoUrl);
    this.companyLogoLoadFailed.set(false);
  }

  /**
   * Acrescenta ou substitui o parâmetro "v" da URL.
   *
   * Isso força o navegador a buscar novamente uma
   * imagem que possui a mesma ObjectKey no S3.
   */
  private createVersionedLogoUrl(logoUrl: string | null | undefined): string | null {
    const normalizedLogoUrl = logoUrl?.trim() ?? '';

    if (!normalizedLogoUrl) {
      return null;
    }

    /**
     * URLs pré-assinadas da AWS não devem receber
     * parâmetros depois de serem assinadas, pois isso
     * pode invalidar a assinatura.
     *
     * Nesse caso, o backend precisa gerar uma nova
     * URL pré-assinada depois de substituir a imagem.
     */
    if (this.isAwsPresignedUrl(normalizedLogoUrl)) {
      return normalizedLogoUrl;
    }

    try {
      const url = new URL(normalizedLogoUrl);

      url.searchParams.set('v', Date.now().toString());

      return url.toString();
    } catch {
      /**
       * Fallback para URLs que não puderem ser
       * interpretadas pela classe URL.
       */
      const cleanedUrl = normalizedLogoUrl.replaceAll(/([?&])v=[^&#]*&?/gi, '$1').replaceAll(/[?&]$/g, '');

      const separator = cleanedUrl.includes('?') ? '&' : '?';

      return `${cleanedUrl}${separator}v=${Date.now()}`;
    }
  }

  private isAwsPresignedUrl(url: string): boolean {
    return /[?&]X-Amz-Signature=/i.test(url) || /[?&]X-Amz-Credential=/i.test(url) || /[?&]AWSAccessKeyId=/i.test(url);
  }

  private saveStoredClientLogo(clientLogo: StoredClientLogo): void {
    if (!this.isBrowser) {
      return;
    }

    try {
      localStorage.setItem(this.getStorageKey(clientLogo.clientId), JSON.stringify(clientLogo));

      localStorage.removeItem(this.getLegacyStorageKey(clientLogo.clientId));
    } catch {
      // LocalStorage indisponível ou sem espaço.
    }
  }

  private getStoredClientLogo(clientId: string): StoredClientLogo | null {
    if (!this.isBrowser) {
      return null;
    }

    try {
      const storedValue = localStorage.getItem(this.getStorageKey(clientId));

      if (!storedValue) {
        return null;
      }

      const parsed = JSON.parse(storedValue) as Partial<StoredClientLogo>;

      if (parsed.clientId !== clientId || typeof parsed.companyName !== 'string') {
        this.removeStoredClientLogo(clientId);
        return null;
      }

      const storedLogoUrl = typeof parsed.logoUrl === 'string' ? parsed.logoUrl : null;

      const logoCachedAt = typeof parsed.logoCachedAt === 'number' ? parsed.logoCachedAt : 0;

      const logoIsValid = Boolean(storedLogoUrl) && Date.now() - logoCachedAt < this.logoCacheDuration;

      return {
        clientId,
        companyName: parsed.companyName,
        logoUrl: logoIsValid ? storedLogoUrl : null,
        logoCachedAt,
      };
    } catch {
      this.removeStoredClientLogo(clientId);
      return null;
    }
  }

  private invalidateStoredLogo(clientId: string): void {
    const storedClientLogo = this.getStoredClientLogo(clientId);

    if (!storedClientLogo) {
      return;
    }

    this.saveStoredClientLogo({
      ...storedClientLogo,
      logoUrl: null,
      logoCachedAt: 0,
    });
  }

  private removeStoredClientLogo(clientId: string): void {
    if (!this.isBrowser) {
      return;
    }

    try {
      localStorage.removeItem(this.getStorageKey(clientId));

      localStorage.removeItem(this.getLegacyStorageKey(clientId));
    } catch {
      // LocalStorage indisponível.
    }
  }

  private getStorageKey(clientId: string): string {
    return `${this.storagePrefix}:${clientId}`;
  }

  private getLegacyStorageKey(clientId: string): string {
    return `${this.legacyStoragePrefix}:${clientId}`;
  }
}
